import React, { useEffect, useState } from "react"
import {
  Row, Col, CardHeader, CardBody, Spinner, Alert, Input, Button,
  Modal, ModalHeader, ModalBody, ModalFooter,
  FormGroup, Label, FormFeedback,
} from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import Select from "react-select"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"
import {
  adminGetCoupons,
  adminCreateCoupon,
  adminUpdateCoupon,
  adminDeleteCoupon,
  adminGetPlans,
} from "../../api/requests/subscription-requests"
import { SubscriptionDeleteModal } from "../modals/subscription/SubscriptionDeleteModal"
import DateInput from "../../components/Common/DateInput";

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const NAVY = "#221C5D"
const ORANGE = "#E56A22"

const discountTypeOptions = [
  { value: "PERCENTAGE", label: "Yüzde (%)" },
  { value: "FIXED_AMOUNT", label: "Sabit Tutar (TL)" },
]

// Türk tutar formatı: 1234.56 → "1.234,56"
const formatTRYAmount = (val) => {
  if (val === "" || val == null) return ""
  const num = typeof val === "number" ? val : parseFloat(val)
  if (isNaN(num)) return ""
  return num.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

// Kullanıcının yazdığı text'i (1.234,56) Number'a çevir; geçersiz/boşsa ""
const parseTRYAmount = (raw) => {
  if (!raw) return ""
  const cleaned = String(raw).replace(/\./g, "").replace(",", ".")
  if (cleaned === "" || cleaned === ".") return ""
  const num = parseFloat(cleaned)
  return isNaN(num) ? "" : num
}

// Sanitize live input: digits, tek virgül, max 2 ondalık + binlik ayraç ekle
const sanitizeTRYInput = (raw) => {
  let s = String(raw || "").replace(/[^\d,.]/g, "").replace(/\./g, "")
  const firstComma = s.indexOf(",")
  if (firstComma !== -1) {
    s = s.slice(0, firstComma + 1) + s.slice(firstComma + 1).replace(/,/g, "")
    s = s.slice(0, firstComma + 3) // max 2 ondalık
  }
  const [intPart, decPart] = s.split(",")
  const intFormatted = intPart ? Number(intPart).toLocaleString("tr-TR") : ""
  return decPart !== undefined ? `${intFormatted},${decPart}` : intFormatted
}

const CouponCodes = () => {
  const navigate = useNavigate()
  const [coupons, setCoupons] = useState([])
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleteCoupon, setDeleteCoupon] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [discountDisplay, setDiscountDisplay] = useState("")

  const styles = `
    .coupons-modern .table thead tr { background-color: #f9f9f9; }
    .coupons-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .coupons-modern .table td { vertical-align: middle; }
    .coupons-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    .coupons-modern .search-input { width: 250px; }
    .coupons-modern .header-actions { gap: .5rem; }
    .coupons-modern .btn-rounded { border-radius: 999px; }
    .coupons-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .coupons-modern .btn-navy:hover { filter: brightness(1.03); }
    .coupons-modern .btn-orange { background: ${ORANGE}; border-color: ${ORANGE}; color: #fff; }
    .coupons-modern .btn-orange:hover { filter: brightness(1.03); }
    .coupons-modern .action-btn { border-radius: 999px; padding: .35rem .6rem; }
    .coupons-modern .pill-input { border-radius: 999px !important; padding: .45rem .9rem; }
    .coupons-modern .code-dark { color: #000 !important; }
    @media (max-width: 576px) {
      .coupons-modern .search-input { width: 100%; }
      .coupons-modern .table { font-size: 12px; }
      .coupons-modern .table td, .coupons-modern .table th { padding: .5rem; }
    }
  `

  document.title = "Kupon Kodları | Tahsilat360"

  const loadData = async (first = false) => {
    try {
      setError(null)
      if (first) setLoading(true); else setRefreshing(true)
      const [couponsRes, plansRes] = await Promise.all([adminGetCoupons(), adminGetPlans()])
      setCoupons(couponsRes.data || [])
      setPlans(plansRes.data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadData(true)
  }, [])

  const planOptions = plans.map((p) => ({ value: p.id, label: p.name }))

  const filteredCoupons = coupons.filter((c) => {
    if (!searchTerm) return true
    const s = searchTerm.toLowerCase()
    return c.code?.toLowerCase().includes(s)
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: editingCoupon?.code || "",
      discountType: editingCoupon?.discountType || "PERCENTAGE",
      discountValue: editingCoupon?.discountValue || "",
      maxUsageCount: editingCoupon?.maxUsageCount || "",
      perCompanyLimit: editingCoupon?.perCompanyLimit || 1,
      validFrom: editingCoupon?.validFrom?.slice(0, 10) || "",
      validUntil: editingCoupon?.validUntil?.slice(0, 10) || "",
      applicablePlanIds: editingCoupon?.applicablePlanIds || [],
      stackableWithDiscount: editingCoupon?.stackableWithDiscount ?? false,
      active: editingCoupon?.active ?? true,
    },
    validationSchema: Yup.object({
      code: Yup.string()
        .matches(/^[A-Z0-9_-]+$/, "Sadece büyük harf, rakam, - ve _ kullanılabilir")
        .min(3, "Kupon kodu en az 3 karakter olmalı")
        .max(20, "Kupon kodu en fazla 20 karakter olabilir")
        .required("Kupon kodu gerekli"),
      discountType: Yup.string().oneOf(["PERCENTAGE", "FIXED_AMOUNT"], "Geçersiz indirim tipi").required("İndirim tipi gerekli"),
      discountValue: Yup.number()
        .min(0.01, "Değer 0'dan büyük olmalı")
        .required("İndirim değeri gerekli")
        .when("discountType", {
          is: "PERCENTAGE",
          then: (schema) => schema.max(100, "Yüzde değeri 100'den fazla olamaz"),
        }),
      maxUsageCount: Yup.number().nullable().min(1, "En az 1 olmalı").transform((v, o) => (o === "" ? null : v)),
      perCompanyLimit: Yup.number().min(1, "En az 1 olmalı").required("Firma başına limit gerekli"),
      validFrom: Yup.date().nullable().transform((v, o) => (o === "" ? null : v)),
      validUntil: Yup.date().nullable().transform((v, o) => (o === "" ? null : v))
        .when("validFrom", {
          is: (val) => val != null,
          then: (schema) => schema.min(Yup.ref("validFrom"), "Bitiş tarihi başlangıçtan önce olamaz"),
        }),
    }),
    onSubmit: async (values) => {
      setSaving(true)
      setSubmitError("")
      try {
        const payload = {
          ...values,
          maxUsageCount: values.maxUsageCount || null,
          validFrom: values.validFrom || null,
          validUntil: values.validUntil || null,
        }
        if (editingCoupon) {
          await adminUpdateCoupon(editingCoupon.id, payload)
          toast.success("Kupon güncellendi")
        } else {
          await adminCreateCoupon(payload)
          toast.success("Kupon oluşturuldu")
        }
        closeModal()
        loadData()
      } catch (err) {
        const msg = err.message || "İşlem başarısız oldu"
        // Hem modal içinde inline göster (kullanıcı modal açıkken kesin görsün),
        // hem de toast — modal kapanırsa toast kalır.
        setSubmitError(msg)
        toast.error(msg)
        // "Aynı kupon kodu tanımlı" gibi spesifik kupon kodu hatası → ilgili input'u da işaretle
        if (msg.toLowerCase().includes("kupon kodu") || msg.toLowerCase().includes("aynı kupon")) {
          formik.setFieldError("code", msg)
          formik.setFieldTouched("code", true, false)
        }
      } finally {
        setSaving(false)
      }
    },
  })

  const handleDelete = async (id) => {
    try {
      await adminDeleteCoupon(id)
      toast.success("Kupon silindi")
      loadData()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setDeleteModalOpen(false)
      setDeleteCoupon(null)
    }
  }

  const openCreate = () => {
    setEditingCoupon(null); formik.resetForm(); setSubmitError("")
    setDiscountDisplay("")
    setModalOpen(true)
  }
  const openEdit = (coupon) => {
    setEditingCoupon(coupon); setSubmitError("")
    setDiscountDisplay(coupon?.discountType === "FIXED_AMOUNT" ? formatTRYAmount(coupon?.discountValue) : String(coupon?.discountValue ?? ""))
    setModalOpen(true)
  }
  const closeModal = () => {
    setModalOpen(false); setEditingCoupon(null); formik.resetForm(); setSubmitError("")
    setDiscountDisplay("")
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("tr-TR") : "-"

  return (
    <div className="page-content coupons-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <Row>
          <Col>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4">
                <div className="d-none d-md-block">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h5 className="mb-1 fw-bold text-dark">Kupon Kodları</h5>
                            <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                                {coupons.length > 0 ? `Toplam ${coupons.length} kupon` : "Kupon bulunamadı"}
                            </p>
                        </div>
                        <Button className="d-flex align-items-center gap-1 btn-rounded btn-navy" onClick={openCreate}>
                            <i className="bx bx-plus" /> Yeni Kupon
                        </Button>
                    </div>
                    <div className="d-flex justify-content-end">
                        <Input type="search" placeholder="Kupon ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input pill-input" />
                    </div>
                </div>
                <div className="d-md-none">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0 fw-semibold">Kuponlar</h5>
                    <Button size="sm" className="d-flex align-items-center gap-1 btn-rounded btn-navy" onClick={openCreate}>
                      <i className="bx bx-plus" /> Ekle
                    </Button>
                  </div>
                  <Input type="search" placeholder="Kupon ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pill-input mb-2" />
                </div>
              </CardHeader>

              <CardBody className="pt-2">
                {error && (
                  <Alert color="danger" className="mb-3 rounded-4">
                    <i className="bx bx-error-circle me-2" />{error}
                    <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={() => loadData()}>Tekrar Dene</button>
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center p-5">
                    <Spinner size="lg" color="primary" />
                    <h6 className="mt-3 text-muted">Kuponlar yükleniyor...</h6>
                  </div>
                ) : (
                  <>
                    <div className={`d-flex justify-content-between align-items-center mb-2 ${refreshing ? "opacity-50" : ""}`}>
                      <div className="text-muted" style={{ fontSize: ".9rem" }}>
                        {filteredCoupons.length > 0 && <>Toplam <strong>{filteredCoupons.length}</strong> kayıt</>}
                      </div>
                      <button className="btn btn-light btn-sm btn-rounded" onClick={() => loadData()} title="Yenile">
                        <i className={`bx bx-refresh ${refreshing ? "bx-spin" : ""}`} />
                      </button>
                    </div>

                    <div className="table-responsive rounded-4 border">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th>Kod</Th>
                            <Th>Tip</Th>
                            <Th>Değer</Th>
                            <Th>Kullanım</Th>
                            <Th className="d-none d-md-table-cell">Geçerlilik</Th>
                            <Th className="d-none d-lg-table-cell">Hedef Paketler</Th>
                            <Th>Durum</Th>
                            <Th className="text-end">İşlemler</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredCoupons.length ? filteredCoupons.map((c) => (
                            <Tr key={c.id} className="border-bottom">
                              <Td><code className="code-dark fw-bold">{c.code}</code></Td>
                              <Td>{c.discountType === "PERCENTAGE" ? "Yüzde" : "Sabit"}</Td>
                              <Td>{c.discountType === "PERCENTAGE" ? `%${c.discountValue}` : `${formatTRYAmount(c.discountValue)} TL`}</Td>
                              <Td>{c.usageCount || 0}/{c.maxUsageCount || "∞"}</Td>
                              <Td className="d-none d-md-table-cell small">{formatDate(c.validFrom)} / {formatDate(c.validUntil)}</Td>
                              <Td className="d-none d-lg-table-cell">
                                {c.applicablePlanIds?.length > 0
                                  ? c.applicablePlanIds.map((pid) => plans.find((p) => p.id === pid)?.name || pid).join(", ")
                                  : "Tümü"}
                              </Td>
                              <Td>
                                <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: c.active ? "#71C30B" : "#E07A7A", color: "#fff" }}>
                                  {c.active ? "Aktif" : "Pasif"}
                                </span>
                              </Td>
                              <Td className="text-end">
                                <div className="d-flex gap-1 justify-content-end flex-wrap">
                                  <button className="btn btn-sm action-btn btn-orange" title="Detay" onClick={() => navigate(`/admin/coupon-codes/${c.id}`)}>
                                    <i className="bx bx-detail" />
                                  </button>
                                  <button className="btn btn-sm action-btn btn-navy" title="Düzenle" onClick={() => openEdit(c)}>
                                    <i className="bx bx-edit" />
                                  </button>
                                  <button className="btn btn-sm action-btn" style={{ background: "#dc3545", borderColor: "#dc3545", color: "#fff" }} title="Sil" onClick={() => { setDeleteCoupon(c); setDeleteModalOpen(true) }}>
                                    <i className="bx bx-trash" />
                                  </button>
                                </div>
                              </Td>
                            </Tr>
                          )) : (
                            <Tr>
                              <Td colSpan="8" className="text-center py-4">
                                <div className="text-muted">
                                  <i className="bx bx-purchase-tag display-4 d-block mb-2" />
                                  Kupon bulunamadı
                                  {searchTerm && <div className="mt-2"><small>Arama teriminizi değiştirmeyi deneyin</small></div>}
                                </div>
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </div>
                  </>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          {editingCoupon ? "Kuponu Düzenle" : "Yeni Kupon Oluştur"}
        </ModalHeader>
        <ModalBody>
          {submitError && (
            <Alert color="danger" className="mb-3">
              <i className="bx bx-error-circle me-2" />
              {submitError}
            </Alert>
          )}
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Kupon Kodu</Label>
                <Input
                  name="code"
                  value={formik.values.code}
                  onChange={(e) => formik.setFieldValue("code", e.target.value.toUpperCase().slice(0, 20))}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.code && !!formik.errors.code}
                  placeholder="YILSONU2024"
                  maxLength={20}
                />
                <FormFeedback>{formik.errors.code}</FormFeedback>
                <small className="text-muted">{formik.values.code.length}/20 karakter</small>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>İndirim Tipi</Label>
                <Input
                  type="select"
                  name="discountType"
                  value={formik.values.discountType}
                  onChange={(e) => {
                    formik.handleChange(e)
                    // Tip değişince mevcut display'i yeni tipe göre yeniden formatla
                    const newType = e.target.value
                    const v = formik.values.discountValue
                    if (newType === "FIXED_AMOUNT") {
                      setDiscountDisplay(v === "" || v == null ? "" : formatTRYAmount(v))
                    } else {
                      setDiscountDisplay(v === "" || v == null ? "" : String(v))
                    }
                  }}
                >
                  {discountTypeOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>İndirim Değeri {formik.values.discountType === "PERCENTAGE" ? "(%)" : "(TL)"}</Label>
                {formik.values.discountType === "FIXED_AMOUNT" ? (
                  <Input
                    type="text"
                    inputMode="decimal"
                    name="discountValue"
                    placeholder="0,00"
                    value={discountDisplay}
                    onChange={(e) => {
                      const display = sanitizeTRYInput(e.target.value)
                      setDiscountDisplay(display)
                      formik.setFieldValue("discountValue", parseTRYAmount(display))
                    }}
                    onBlur={(e) => {
                      // Blur'da kanonik formata çevir (örn. "1.234,5" → "1.234,50")
                      const num = parseTRYAmount(discountDisplay)
                      if (num !== "") {
                        setDiscountDisplay(
                          Number(num).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        )
                      }
                      formik.handleBlur(e)
                    }}
                    invalid={formik.touched.discountValue && !!formik.errors.discountValue}
                  />
                ) : (
                  <Input
                    type="number"
                    step="0.01"
                    name="discountValue"
                    value={formik.values.discountValue}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.discountValue && !!formik.errors.discountValue}
                  />
                )}
                <FormFeedback>{formik.errors.discountValue}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Maks. Kullanım</Label>
                <Input type="number" name="maxUsageCount" placeholder="Boş = sınırsız" value={formik.values.maxUsageCount} onChange={formik.handleChange} onBlur={formik.handleBlur} invalid={formik.touched.maxUsageCount && !!formik.errors.maxUsageCount} />
                <FormFeedback>{formik.errors.maxUsageCount}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Firma Başına Limit</Label>
                <Input type="number" name="perCompanyLimit" min={1} value={formik.values.perCompanyLimit} onChange={formik.handleChange} onBlur={formik.handleBlur} invalid={formik.touched.perCompanyLimit && !!formik.errors.perCompanyLimit} />
                <FormFeedback>{formik.errors.perCompanyLimit}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup className="d-flex flex-column">
                <Label>&nbsp;</Label>
                <div className="form-check mt-2">
                  <input type="checkbox" className="form-check-input" id="stackableWithDiscount" name="stackableWithDiscount" checked={formik.values.stackableWithDiscount} onChange={formik.handleChange} />
                  <label className="form-check-label" htmlFor="stackableWithDiscount">İndirimle birleşebilir</label>
                </div>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Geçerlilik Başlangıcı</Label>
                <DateInput value={formik.values.validFrom} onChange={(iso) => formik.setFieldValue("validFrom", iso)} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Geçerlilik Bitişi</Label>
                <DateInput value={formik.values.validUntil} onChange={(iso) => formik.setFieldValue("validUntil", iso)} />
                <FormFeedback>{formik.errors.validUntil}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Geçerli Paketler</Label>
            <Select
              isMulti
              options={planOptions}
              value={planOptions.filter((o) => formik.values.applicablePlanIds.includes(o.value))}
              onChange={(selected) => formik.setFieldValue("applicablePlanIds", selected ? selected.map((s) => s.value) : [])}
              placeholder="Tüm paketler için boş bırakın..."
              noOptionsMessage={() => "Paket bulunamadı"}
            />
          </FormGroup>
          <div className="form-check">
            <input type="checkbox" className="form-check-input" id="couponActive" name="active" checked={formik.values.active} onChange={formik.handleChange} />
            <label className="form-check-label" htmlFor="couponActive">Aktif</label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>İptal</Button>
          <Button color="primary" disabled={saving} onClick={formik.handleSubmit}>
            {saving ? <Spinner size="sm" /> : editingCoupon ? "Güncelle" : "Oluştur"}
          </Button>
        </ModalFooter>
      </Modal>

      <SubscriptionDeleteModal
        isOpen={deleteModalOpen}
        toggle={() => { setDeleteModalOpen(false); setDeleteCoupon(null) }}
        title="Kupon Silme Onayı"
        icon="bx-purchase-tag"
        message="Aşağıdaki kuponu kalıcı olarak silmek istediğinizden emin misiniz?"
        item={deleteCoupon}
        renderInfo={(c) => (
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-1 text-dark"><code>{c.code}</code></h6>
              <div className="text-muted small">
                {c.discountType === "PERCENTAGE" ? `%${c.discountValue} indirim` : `${c.discountValue} TL indirim`}
                {" - "}Kullanım: {c.usageCount || 0}/{c.maxUsageCount || "∞"}
              </div>
            </div>
            <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: c.active ? "#71C30B" : "#E07A7A", color: "#fff" }}>
              {c.active ? "Aktif" : "Pasif"}
            </span>
          </div>
        )}
        onConfirm={handleDelete}
      />
    </div>
  )
}

export default CouponCodes
